import { Fragment, useMemo, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  ArrowUpTrayIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { Candidate, RoleStatus } from "../../../Models";
import { getNextStatus } from "../../../Helpers";
import {
  CandidateEmailImportResult,
  ResolveCandidatesByEmailList,
} from "../../../api/candidates.api";
import { Spinner } from "../../Shared/Spinner";
import useApiToken from "../../../hooks/useApiToken";

const ACCEPTED_EXTENSIONS = [".xlsx", ".csv", ".tsv", ".txt"];

export type ImportBuckets = {
  /** Matched, advanceable, and sitting in the stage the grid is currently showing. */
  inStage: Candidate[];
  /** Matched and advanceable, but in a different stage than the one on screen. */
  otherStage: Candidate[];
  /** Matched but with nowhere to advance to — hired, rejected, or at the end of the pipeline. */
  terminal: Candidate[];
};

/**
 * Splits matched candidates the same way the Advance button does, so the import summary can never
 * promise a row that the bulk action would then silently drop: a candidate with no next status is
 * already discarded by the existing `candidatesWithAction` filter.
 *
 * `activeStageIds` are the statuses the sidebar is filtered to. With the "All" status filter there
 * is no single stage on screen, so every advanceable match counts as in-stage.
 */
export function classifyImportedCandidates(
  candidates: Candidate[],
  roleStatuses: RoleStatus[],
  activeStageIds: string[],
  statusFilterIsAll: boolean
): ImportBuckets {
  const buckets: ImportBuckets = { inStage: [], otherStage: [], terminal: [] };
  const activeStages = new Set(activeStageIds);

  candidates.forEach((candidate) => {
    const nextStatus = getNextStatus(roleStatuses, candidate.candidateStatuses);
    if (!nextStatus) {
      buckets.terminal.push(candidate);
      return;
    }
    if (statusFilterIsAll || activeStages.size === 0 || activeStages.has(candidate.latestStatusId))
      buckets.inStage.push(candidate);
    else buckets.otherStage.push(candidate);
  });

  return buckets;
}

/** Reads the status ids the candidate sidebar is filtered to out of its generated filter query. */
export function activeStageIdsFromFilterQuery(filterQuery: string): string[] {
  const matches = filterQuery.matchAll(/latestStatusId\s+eq\s+"([^"]+)"/g);
  return Array.from(matches, (m) => m[1]);
}

function SummaryRow({
  count,
  label,
  detail,
  tone,
  values,
}: {
  count: number;
  label: string;
  detail?: string;
  tone: "included" | "excluded" | "warning";
  values?: string[];
}) {
  const [expanded, setExpanded] = useState(false);
  if (count === 0) return null;

  const countTone =
    tone === "included"
      ? "text-green-700 dark:text-green-400"
      : tone === "warning"
        ? "text-amber-700 dark:text-amber-400"
        : "text-gray-500 dark:text-gray-400";

  return (
    <div className="border-b border-gray-100 py-2 last:border-b-0 dark:border-gray-700">
      <div className="flex items-baseline gap-3">
        <span className={`w-14 shrink-0 text-right text-sm font-semibold tabular-nums ${countTone}`}>
          {count}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm text-gray-800 dark:text-gray-200">{label}</p>
          {detail && <p className="text-xs text-gray-500 dark:text-gray-400">{detail}</p>}
        </div>
        {values && values.length > 0 && (
          <button
            type="button"
            className="shrink-0 text-xs font-medium text-entntblue hover:underline dark:text-blue-400"
            onClick={() => setExpanded((prev) => !prev)}
          >
            {expanded ? "hide" : "view"}
          </button>
        )}
      </div>
      {expanded && values && (
        <div className="mt-2 ml-[4.25rem]">
          <textarea
            readOnly
            className="thin-scroll h-24 w-full rounded border border-gray-200 bg-gray-50 p-2 font-mono text-xs text-gray-700 dark:border-gray-600 dark:bg-darkbg dark:text-gray-300"
            value={values.join("\n")}
            onFocus={(e) => e.currentTarget.select()}
          />
        </div>
      )}
    </div>
  );
}

type Props = {
  open: boolean;
  onClose: () => void;
  roleId: string;
  roleStatuses: RoleStatus[];
  /** Statuses the sidebar is filtered to; drives the in-stage / other-stage split. */
  activeStageIds: string[];
  statusFilterIsAll: boolean;
  sortQuery: string;
  /**
   * Hands the matched candidates and the pre-selection to the grid. The caller decides what to do
   * with them — this modal never advances anyone.
   */
  onApply: (
    result: CandidateEmailImportResult,
    candidates: Candidate[],
    preselected: Candidate[]
  ) => void;
};

export default function ImportEmailListModal({
  open,
  onClose,
  roleId,
  roleStatuses,
  activeStageIds,
  statusFilterIsAll,
  sortQuery,
  onApply,
}: Props) {
  const token = useApiToken();
  const cancelButtonRef = useRef(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [pastedEmails, setPastedEmails] = useState("");
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [result, setResult] = useState<CandidateEmailImportResult | null>(null);
  const [includeOtherStages, setIncludeOtherStages] = useState(false);

  const buckets = useMemo(
    () =>
      result
        ? classifyImportedCandidates(
            result.candidates,
            roleStatuses,
            activeStageIds,
            statusFilterIsAll
          )
        : null,
    [result, roleStatuses, activeStageIds, statusFilterIsAll]
  );

  const activeStageNames = useMemo(
    () =>
      activeStageIds
        .map((id) => roleStatuses.find((rs) => rs.id === id)?.name)
        .filter((name): name is string => Boolean(name)),
    [activeStageIds, roleStatuses]
  );

  const stageLabel =
    statusFilterIsAll || activeStageNames.length === 0
      ? "this role"
      : activeStageNames.join(", ");

  const reset = () => {
    setFile(null);
    setPastedEmails("");
    setResult(null);
    setErrorMessage(null);
    setIncludeOtherStages(false);
    setLoading(false);
  };

  const handleClose = () => {
    if (loading) return;
    reset();
    onClose();
  };

  const read = async (emailColumnIndex?: number) => {
    if (!token) return;
    setLoading(true);
    setErrorMessage(null);
    try {
      const response = await ResolveCandidatesByEmailList(
        roleId,
        token,
        file ? { file, emailColumnIndex } : { emails: pastedEmails },
        sortQuery
      );
      setResult(response);
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "Failed to read the email list.");
    } finally {
      setLoading(false);
    }
  };

  const selectFile = (selected: File | null) => {
    setErrorMessage(null);
    if (!selected) return;
    const extension = selected.name.slice(selected.name.lastIndexOf(".")).toLowerCase();
    if (!ACCEPTED_EXTENSIONS.includes(extension)) {
      setErrorMessage(
        extension === ".xls"
          ? "The legacy .xls format is not supported. Open the file in Excel and save it as .xlsx."
          : `Unsupported file type. Upload one of: ${ACCEPTED_EXTENSIONS.join(", ")}.`
      );
      return;
    }
    setFile(selected);
    setPastedEmails("");
  };

  const apply = () => {
    if (!result || !buckets) return;
    const preselected = includeOtherStages
      ? [...buckets.inStage, ...buckets.otherStage]
      : buckets.inStage;
    const excludedIds = new Set(
      includeOtherStages ? [] : buckets.otherStage.map((c) => c.id)
    );
    const candidates = result.candidates.filter((c) => !excludedIds.has(c.id));
    onApply(result, candidates, preselected);
    reset();
  };

  const canRead = Boolean(file) || pastedEmails.trim().length > 0;

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        initialFocus={cancelButtonRef}
        onClose={handleClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-400 bg-opacity-50 transition-opacity dark:bg-opacity-20" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:max-w-2xl dark:bg-darkbglight">
                <div className="px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                      <ArrowUpTrayIcon className="h-6 w-6 text-blue-600" aria-hidden="true" />
                    </div>
                    <div className="mt-3 w-full text-center sm:ml-4 sm:mt-0 sm:text-left">
                      <Dialog.Title
                        as="h3"
                        className="text-base font-semibold leading-6 text-gray-900 dark:text-gray-200"
                      >
                        Import email list
                      </Dialog.Title>

                      {!result ? (
                        <>
                          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            Upload a spreadsheet of email addresses to filter this role's candidates
                            down to that list. Nothing is advanced until you confirm from the grid.
                          </p>

                          <div
                            className={`mt-4 rounded-md border-2 border-dashed p-6 text-center transition-colors ${
                              dragging
                                ? "border-entntblue bg-blue-50 dark:bg-entntblue/10"
                                : "border-gray-300 dark:border-gray-600"
                            }`}
                            onDragOver={(e) => {
                              e.preventDefault();
                              setDragging(true);
                            }}
                            onDragLeave={() => setDragging(false)}
                            onDrop={(e) => {
                              e.preventDefault();
                              setDragging(false);
                              selectFile(e.dataTransfer.files?.[0] ?? null);
                            }}
                          >
                            {file ? (
                              <div className="flex items-center justify-center gap-2">
                                <DocumentTextIcon className="h-5 w-5 text-gray-400" />
                                <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                  {file.name}
                                </span>
                                <button
                                  type="button"
                                  className="text-xs font-medium text-entntblue hover:underline dark:text-blue-400"
                                  onClick={() => setFile(null)}
                                >
                                  remove
                                </button>
                              </div>
                            ) : (
                              <>
                                <p className="text-sm text-gray-600 dark:text-gray-300">
                                  Drop a file here, or{" "}
                                  <button
                                    type="button"
                                    className="font-semibold text-entntblue hover:underline dark:text-blue-400"
                                    onClick={() => fileInputRef.current?.click()}
                                  >
                                    browse
                                  </button>
                                </p>
                                <p className="mt-1 text-xs text-gray-400">
                                  {ACCEPTED_EXTENSIONS.join(", ")}
                                </p>
                              </>
                            )}
                            <input
                              ref={fileInputRef}
                              type="file"
                              className="hidden"
                              accept={ACCEPTED_EXTENSIONS.join(",")}
                              onChange={(e) => selectFile(e.target.files?.[0] ?? null)}
                            />
                          </div>

                          <div className="mt-4">
                            <label className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                              Or paste addresses
                            </label>
                            <textarea
                              className="thin-scroll mt-1 h-20 w-full rounded-md border border-gray-300 p-2 text-sm dark:border-gray-600 dark:bg-darkbg dark:text-gray-200"
                              placeholder="one per line, or separated by commas"
                              value={pastedEmails}
                              disabled={Boolean(file)}
                              onChange={(e) => setPastedEmails(e.target.value)}
                            />
                          </div>
                        </>
                      ) : (
                        <div className="mt-2">
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Read{" "}
                            <span className="font-semibold text-gray-700 dark:text-gray-200">
                              {result.emailsParsed} address
                              {result.emailsParsed === 1 ? "" : "es"}
                            </span>{" "}
                            from {result.fileName ?? "the pasted list"}
                            {result.totalRows > 0 && ` (${result.totalRows} rows)`}.
                          </p>

                          {result.columns.length > 1 && (
                            <div className="mt-3">
                              <label className="text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                Email column
                              </label>
                              <div className="mt-1 flex items-center gap-2">
                                <select
                                  className="rounded-md border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-darkbg dark:text-gray-200"
                                  value={result.emailColumnIndex}
                                  disabled={loading || !file}
                                  onChange={(e) => read(Number(e.target.value))}
                                >
                                  {result.columns.map((column, index) => (
                                    <option key={`${column}-${index}`} value={index}>
                                      {column}
                                    </option>
                                  ))}
                                </select>
                                {loading && <Spinner height="h-4" width="w-4" />}
                              </div>
                              {result.previewRows.length > 0 && (
                                <div className="thin-scroll mt-2 max-h-32 overflow-auto rounded border border-gray-200 dark:border-gray-600">
                                  <table className="min-w-full text-xs">
                                    <tbody>
                                      {result.previewRows.map((row, rowIndex) => (
                                        <tr
                                          key={rowIndex}
                                          className="border-b border-gray-100 last:border-b-0 dark:border-gray-700"
                                        >
                                          {row.map((cell, cellIndex) => (
                                            <td
                                              key={cellIndex}
                                              className={`max-w-[12rem] truncate px-2 py-1 ${
                                                cellIndex === result.emailColumnIndex
                                                  ? "bg-blue-50 font-medium text-gray-800 dark:bg-entntblue/20 dark:text-gray-200"
                                                  : "text-gray-500 dark:text-gray-400"
                                              }`}
                                            >
                                              {cell}
                                            </td>
                                          ))}
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          )}

                          {result.truncated && (
                            <div className="mt-3 flex items-start gap-2 rounded-md bg-amber-50 p-2 text-xs text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                              <ExclamationTriangleIcon className="h-4 w-4 shrink-0" />
                              <span>
                                The file was longer than the 50,000-row limit and was cut short.
                                Only the rows above were read.
                              </span>
                            </div>
                          )}

                          <div className="mt-4 rounded-md border border-gray-200 px-3 dark:border-gray-700">
                            <SummaryRow
                              count={buckets?.inStage.length ?? 0}
                              label={`matched in ${stageLabel}`}
                              detail="selected and ready to advance"
                              tone="included"
                            />
                            <SummaryRow
                              count={buckets?.otherStage.length ?? 0}
                              label="matched in a different stage"
                              detail={
                                includeOtherStages
                                  ? "included — each will advance from its own stage"
                                  : "not shown; tick below to include them"
                              }
                              tone="warning"
                              values={buckets?.otherStage.map((c) => c.email)}
                            />
                            <SummaryRow
                              count={buckets?.terminal.length ?? 0}
                              label="already hired, rejected, or at the last stage"
                              detail="nowhere left to advance to — skipped"
                              tone="excluded"
                              values={buckets?.terminal.map((c) => c.email)}
                            />
                            <SummaryRow
                              count={result.emailsNotOnRoleCount}
                              label="known candidates who have not applied to this role"
                              tone="excluded"
                              values={result.emailsNotOnRole}
                            />
                            <SummaryRow
                              count={result.emailsNotFoundCount}
                              label="no candidate found for this address"
                              detail="usually a typo in the sheet"
                              tone="excluded"
                              values={result.emailsNotFound}
                            />
                            <SummaryRow
                              count={result.duplicateCount}
                              label="addresses listed more than once"
                              detail="counted once"
                              tone="excluded"
                              values={result.duplicateEmails}
                            />
                            <SummaryRow
                              count={result.invalidCount}
                              label="cells that are not email addresses"
                              detail="ignored"
                              tone="excluded"
                              values={result.invalidValues}
                            />
                          </div>

                          {(buckets?.otherStage.length ?? 0) > 0 && (
                            <label className="mt-3 flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                              <input
                                type="checkbox"
                                className="mt-0.5 rounded border-gray-300 text-entntblue focus:ring-entntblue"
                                checked={includeOtherStages}
                                onChange={(e) => setIncludeOtherStages(e.target.checked)}
                              />
                              <span>
                                Also include the {buckets?.otherStage.length} candidate
                                {buckets?.otherStage.length === 1 ? "" : "s"} in other stages.
                                Advancing them moves each one forward from its own stage, not from{" "}
                                {stageLabel}.
                              </span>
                            </label>
                          )}
                        </div>
                      )}

                      {errorMessage && (
                        <div className="mt-3 flex items-start gap-2 rounded-md bg-red-50 p-3 text-sm text-red-800 dark:bg-red-900/30 dark:text-red-300">
                          <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
                          <span>{errorMessage}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 dark:bg-darkbg">
                  {result ? (
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50 sm:ml-3 sm:w-auto"
                      onClick={apply}
                      disabled={
                        loading ||
                        (buckets?.inStage.length ?? 0) +
                          (includeOtherStages ? buckets?.otherStage.length ?? 0 : 0) ===
                          0
                      }
                    >
                      Show{" "}
                      {(buckets?.inStage.length ?? 0) +
                        (includeOtherStages ? buckets?.otherStage.length ?? 0 : 0)}{" "}
                      candidate
                      {(buckets?.inStage.length ?? 0) +
                        (includeOtherStages ? buckets?.otherStage.length ?? 0 : 0) ===
                      1
                        ? ""
                        : "s"}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50 sm:ml-3 sm:w-auto"
                      onClick={() => read()}
                      disabled={!canRead || loading || !token}
                    >
                      {loading && <Spinner height="h-4" width="w-4" />}
                      Read list
                    </button>
                  )}
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 sm:mt-0 sm:w-auto dark:bg-darkbglight dark:text-gray-200 dark:ring-gray-600"
                    onClick={result ? () => setResult(null) : handleClose}
                    disabled={loading}
                    ref={cancelButtonRef}
                  >
                    {result ? "Back" : "Cancel"}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
